import axios, {AxiosInstance, AxiosPromise, AxiosRequestConfig} from "axios";
import authService from "common/services/authService";

const http: AxiosInstance = axios.create();

http.interceptors.request.use(async (config: AxiosRequestConfig) => {
	const token = await authService.getAccessToken();

	if (token) {
		config.headers = {
			...config.headers,
			Authorization: `Bearer ${token}`
		};
	}

	return config;
});

http.interceptors.response.use(
	(response) => response,
	(error) => {
		if (error && error.response && error.response.status === 401) {
			authService.forceLogout();
		}

		return Promise.reject(error);
	}
);

/**
 * Local API base class. All subclasses share one Axios instance so
 * authentication and unauthorized-response handling live in one place.
 */
export default class BaseAPI {
	private baseUrl: string;
	protected _basePath: string = "";

	constructor(baseUrl: string = "") {
		this.baseUrl = baseUrl;
	}

	protected get basePath(): string {
		return this._basePath;
	}

	protected set basePath(value: string) {
		this._basePath = value && value.startsWith("/") ? value.slice(1) : value;
	}

	protected get<T = any>(url: string, config: AxiosRequestConfig = {}): AxiosPromise<T> {
		return this.request<T>({...config, method: "get", url});
	}

	protected post<T = any>(url: string, config: AxiosRequestConfig = {}): AxiosPromise<T> {
		return this.request<T>({...config, method: "post", url});
	}

	protected patch<T = any>(url: string, config: AxiosRequestConfig = {}): AxiosPromise<T> {
		return this.request<T>({...config, method: "patch", url});
	}

	protected put<T = any>(url: string, config: AxiosRequestConfig = {}): AxiosPromise<T> {
		return this.request<T>({...config, method: "put", url});
	}

	protected delete<T = any>(url: string, config: AxiosRequestConfig = {}): AxiosPromise<T> {
		return this.request<T>({...config, method: "delete", url});
	}

	protected request<T = any>(config: AxiosRequestConfig): AxiosPromise<T> {
		return http.request<T>({
			baseURL: this.baseUrl,
			...config
		});
	}

	protected pathify(tail: string | string[]): string {
		const normalizedTail = (Array.isArray(tail) ? tail.join("/") : tail).replace(/^\/+/, "");
		return `${this.basePath ? `/${this.basePath}` : ""}/${normalizedTail}`;
	}
}
